import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, History, Pencil, Plus, Zap } from 'lucide-react';
import Button from 'components/crm/ui/Button';
import CrmScope from 'components/crm/ui/CrmScope';
import Spinner from 'components/crm/ui/Spinner';
import ToastProvider, { useToast } from 'components/crm/ui/Toast';
import PageForm from 'components/pageForm/PageForm';
import AddProspectDrawer from 'components/prospection/AddProspectDrawer';
import CallList from 'components/prospection/CallList';
import CallSheetPlaceholder from 'components/prospection/CallSheetPlaceholder';
import GlobalListPanel from 'components/prospection/GlobalListPanel';
import IdlePanel from 'components/prospection/IdlePanel';
import SidePanel from 'components/prospection/SidePanel';
import { dial } from 'components/record/callAction';
import {
  isCanceledRequest,
  prospectionErrorText,
} from 'helper/prospectionErrors';
import useText from 'hooks/useText';
import {
  dayState,
  lineOfCompany,
  postponedToast,
} from 'pages/prospection/callListItems';
import useCallDay from 'pages/prospection/useCallDay';
import {
  addToCallDay,
  listProspects,
  removeFromCallDay,
  restoreToCallDay,
} from 'server/crm/prospectionApi';

const ENTRY_PARAMS = ['prospect', 'appeler'];

const findByCompany = (prospects, companyId) =>
  (prospects || []).find((item) => item.company?.id === companyId);

// The screen itself, under the kit's ToastProvider so that it can toast.
const CallDayScreen = () => {
  const { text } = useText('prospection');
  const toast = useToast();
  const {
    day,
    now,
    loading,
    error,
    reloadDay,
    globalProspects,
    globalLoading,
    reloadGlobal,
  } = useCallDay();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editing, setEditing] = useState(false);
  const [openCall, setOpenCall] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [globalOpen, setGlobalOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // The company of ?appeler=, kept until the day and the global list are read.
  const pendingCallId = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Links from other screens: read once, then taken out of the address.
  useEffect(() => {
    if (!ENTRY_PARAMS.some((name) => searchParams.has(name))) return;
    const prospect = searchParams.get('prospect');
    const appeler = searchParams.get('appeler');
    if (prospect) setHighlightId(prospect);
    if (appeler) pendingCallId.current = appeler;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        ENTRY_PARAMS.forEach((name) => next.delete(name));
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  // The call of ?appeler= is already dialed by the screen that sent here: only
  // its sheet opens, from the day, the global list, or any prospect of one's own.
  useEffect(() => {
    const companyId = pendingCallId.current;
    if (!companyId || !day || globalProspects === null) return;
    pendingCallId.current = null;
    const line = lineOfCompany(day.pending, companyId);
    if (line) {
      setHighlightId(null);
      setEditing(false);
      setOpenCall({ company: line.company, line });
      return;
    }
    const prospect = findByCompany(globalProspects, companyId);
    if (prospect) {
      setOpenCall({ company: prospect.company, line: null });
      return;
    }
    listProspects().then(
      (result) => {
        const found = findByCompany(result?.prospects, companyId);
        if (found && mounted.current) {
          setOpenCall({ company: found.company, line: null });
        }
      },
      // Not one of one's prospects, or gone: the link opens nothing.
      () => {},
    );
  }, [day, globalProspects]);

  const comingSoon = useCallback(() => toast(text('comingSoon')), [
    toast,
    text,
  ]);
  const fail = useCallback(
    (failure) => {
      if (isCanceledRequest(failure)) return;
      toast(prospectionErrorText(text, failure), 'error');
    },
    [toast, text],
  );
  const reloadAll = () => {
    reloadDay();
    reloadGlobal();
  };

  // The highlight from another screen ends with the first call: it does not
  // come back when the sheet closes.
  const call = (line) => {
    setHighlightId(null);
    setEditing(false);
    dial(line.company?.phone);
    setOpenCall({ company: line.company, line });
  };

  const launchSession = () => {
    setEditing(false);
    comingSoon();
  };

  const restore = (undo) => restoreToCallDay(undo).then(reloadAll, fail);

  const remove = (line) =>
    removeFromCallDay({ callbackId: line.id }).then((result) => {
      if (line.company?.id && line.company.id === highlightId) {
        setHighlightId(null);
      }
      reloadAll();
      const company = line.company?.name || '-';
      let message = text('toasts.removed', { company });
      if (result?.removed === 'postponed') {
        const { key, values } = postponedToast(result.dueAt, new Date());
        message = text(key, { ...values, company });
      }
      toast(message, 'success', {
        label: text('toasts.undo'),
        onClick: () => restore(result.undo),
      });
    }, fail);

  // Calls added to the day belong to its session (R14).
  const addPayload = (prospect) => ({
    companyId: prospect.company.id,
    sessionId: day?.daySession?.id,
  });

  const addSuggestion = (prospect) =>
    addToCallDay(addPayload(prospect)).then(() => {
      toast(text('toasts.added', { company: prospect.company.name || '-' }));
      reloadAll();
    }, fail);

  // Rejected when the add fails, so that the panel keeps its button.
  const addFromGlobal = (prospect) =>
    addToCallDay(addPayload(prospect)).then(
      () => {
        reloadDay();
      },
      (failure) => {
        fail(failure);
        throw failure;
      },
    );

  const closeGlobal = () => {
    setGlobalOpen(false);
    reloadGlobal();
  };

  const openCompanyId = openCall?.company?.id ?? null;
  const highlightedLine =
    day && highlightId ? lineOfCompany(day.pending, highlightId) : null;

  // Each panel is its own frame: the column around them has none.
  const renderSide = () => {
    if (openCall) {
      return (
        <CallSheetPlaceholder
          company={openCall.company}
          onClose={() => setOpenCall(null)}
        />
      );
    }
    if (!day) {
      return loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : null;
    }
    if (day.pending?.length) {
      return (
        <IdlePanel
          pending={day.pending}
          highlightedLine={highlightedLine}
          onLaunch={launchSession}
          onCall={call}
        />
      );
    }
    return (
      <SidePanel
        day={day}
        state={dayState(day)}
        prospects={globalProspects}
        now={now}
        onAdd={addSuggestion}
      />
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-[72px] shrink-0 items-center justify-end gap-[10px] border-b border-line px-8">
        <Button variant="secondary" icon={History} onClick={comingSoon}>
          {text('commands.sessions')}
        </Button>
        {editing ? (
          <Button icon={Check} onClick={() => setEditing(false)}>
            {text('commands.finishEdit')}
          </Button>
        ) : (
          <Button
            variant="secondary"
            icon={Pencil}
            disabled={!day}
            onClick={() => setEditing(true)}
          >
            {text('commands.edit')}
          </Button>
        )}
        <Button icon={Plus} disabled={!day} onClick={() => setAddOpen(true)}>
          {text('commands.addProspect')}
        </Button>
      </div>
      <div className="flex min-h-0 flex-1 gap-6 px-8 pb-6 pt-5">
        <section className="w-[520px] shrink-0 overflow-y-auto">
          <CallList
            day={day}
            loading={loading}
            error={error}
            now={now}
            editing={editing}
            selectedCompanyId={openCompanyId ?? highlightId}
            openCompanyId={openCompanyId}
            queueActions={null}
            onCall={call}
            onRemove={remove}
            onOpenGlobal={() => setGlobalOpen(true)}
            onAddProspect={() => setAddOpen(true)}
          />
        </section>
        <section className="flex min-h-0 min-w-0 flex-1 flex-col">
          {renderSide()}
        </section>
      </div>
      <GlobalListPanel
        open={globalOpen}
        onClose={closeGlobal}
        prospects={globalProspects}
        loading={globalLoading}
        counts={day?.counts}
        onAdd={addFromGlobal}
        onShowAllProspects={comingSoon}
      />
      <AddProspectDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={reloadAll}
      />
    </div>
  );
};

// Today's call list (JBWEB-1394): the list on the left, and on the right the
// call sheet, the waiting panel or the end panel. The app.admin shell (menu,
// Intercom) stays; everything inside it is drawn with the CRM kit.
const ProspectionPage = () => {
  const { text } = useText('prospection');

  const title = (
    <span className="flex items-center gap-2">
      <Zap size={20} className="fill-current" />
      {text('title')}
    </span>
  );

  return (
    <PageForm title={title}>
      <CrmScope className="flex min-h-0 flex-1 flex-col">
        <ToastProvider>
          <CallDayScreen />
        </ToastProvider>
      </CrmScope>
    </PageForm>
  );
};

export default ProspectionPage;
